import React, { useContext } from 'react';
import { Button } from 'src/components/button';
import { AppContext } from 'src/components/app-context';
import { Translate } from 'src/components/translate';

import {
  ErrorMessageWrapper,
  ErrorMessageMedia,
  ErrorMessageContent,
  ErrorMessageButton,
  ErrorMessageErrorLabel,
  ErrorMessageSupportLabel,
  ErrorMessageTitle,
  ErrorMessageSubtitle,
  PageNotFoundWrapper,
  Header,
  Body,
  ButtonGroup,
} from './styles';
import { TonalButton } from '@prisjakt/ui/button/variants';
import { useSelector } from 'react-redux';
import type { AppState } from 'src/reducers';
import type { Campaign } from 'src/types';

type ErrorMessageProps = {
  status?: number;
  onRetry?: () => void;
};

/*
  When using compose from DataContainer we can only get
  the contexts requested by the DataContainer component.
*/
const ErrorMessage: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ErrorMessageProps>>
> = ({ status = 500, onRetry }) => {
  const {
    market: { cdn, locale },
    t,
  } = useContext(AppContext);

  const campaigns = useSelector((state: AppState) => state.globals.campaigns);

  const renderRetryButton = () => {
    if (onRetry) {
      return (
        <Button onClick={onRetry}>
          {t('errors', 'error-page.retry-button')}
        </Button>
      );
    }

    return (
      <a href="/">
        <Button>{t('errors', 'error-page.go-home-button')}</Button>
      </a>
    );
  };

  const renderStatusMessage = (centered = false) => {
    if (!status) {
      return null;
    }

    return (
      <ErrorMessageErrorLabel $centered={centered}>
        {t('errors', 'error-page.error-code', { code: status })}
      </ErrorMessageErrorLabel>
    );
  };

  const makeButton = (text: string, link = '') => {
    return (
      <TonalButton container="a" containerProps={{ href: link }}>
        {text}
      </TonalButton>
    );
  };

  const getDailyDealsLink = () => {
    const dailyDeals = campaigns?.active.find(
      (campaign: Campaign) => campaign.type === 'OFFER'
    );

    const link = dailyDeals?.web_uri?.responsive;
    return link || '';
  };

  const renderPageNotFoundButtons = () => {
    return (
      <ButtonGroup>
        {makeButton(t('campaigns', 'daily-deals.title'), getDailyDealsLink())}
        {locale === 'sv'
          ? makeButton('Guider och topplistor från våra experter', '/experter')
          : null}
        {makeButton(t('landing', 'hot-products.title'), '/top-100--cv100')}
      </ButtonGroup>
    );
  };

  const renderErrorPage = () => {
    if (status === 404 || status === 410) {
      return (
        <PageNotFoundWrapper>
          <Header>{t('errors', 'error-page.page-not-found-title')}</Header>
          <Body>{t('errors', 'error-page.page-not-found-subtitle')}</Body>
          {renderPageNotFoundButtons()}
          <img
            src="https://cdn.pji.nu/g/rfe/icons/Error_404_cute_logo.png"
            alt=""
          />
          {renderStatusMessage()}
        </PageNotFoundWrapper>
      );
    }

    return (
      <ErrorMessageWrapper>
        <ErrorMessageMedia>
          <img alt="Error" src={`${cdn}/rfe/g/error_page_io.png`} />
        </ErrorMessageMedia>

        <ErrorMessageContent>
          <div>
            <ErrorMessageTitle>
              {t('errors', 'error-page.title')}
            </ErrorMessageTitle>
            <ErrorMessageSubtitle>
              {t('errors', 'internal.unknown')}
            </ErrorMessageSubtitle>
            <ErrorMessageButton>{renderRetryButton()}</ErrorMessageButton>
          </div>

          {status >= 500 && (
            <ErrorMessageSupportLabel>
              <Translate domain="errors">support.text-link.md</Translate>
            </ErrorMessageSupportLabel>
          )}
          {renderStatusMessage(true)}
        </ErrorMessageContent>
      </ErrorMessageWrapper>
    );
  };

  return (
    <div data-test="ErrorMessage" data-status={status}>
      {renderErrorPage()}
    </div>
  );
};

export default ErrorMessage;
